import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { NavCategorySet } from '../../../models/NavCategorySet.model';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { assetPathFnState, pageContextState, mobileMenuOpenState } from '../../../recoil/atoms';
import { Link } from '../../../root/components/Link';
import { trackMobileNavMenuOpened } from '../../../tracking/events';
import { ImageLoader } from '../../../product_detail/components/ImageLoader';
import { SizingToolState } from '../../../models/actionStates';
import { PageContext } from '../../../enums/PageContext.enum';
import { SelectionType } from '../../../enums/SelectionType.enum';
import classnames from 'classnames';
import { ClerkLoaded, SignedIn, SignedOut, useClerk, UserButton } from '@clerk/clerk-react';
import { useCustomer } from '../../../hooks/useCustomer';
import { useNavCategories } from '../../../hooks/actions/useNavCategories';
import { NavCategoryContext } from '../../../enums/NavCategoryContext.enum';
import { sizingToolState } from '../../../recoil/selectors';
import { useProductSelection } from '../../../hooks/useProductSelection';
import { MobileNavMenuCategory } from './MobileNavMenuCategory';
import { MenuItem } from './MobileHeader';
import { Button } from '../../../root/components/buttons/Button';
import { ImageButton } from '../../../root/components/buttons/ImageButton';
import { lockParentWithScrollingChild, unlockParentWithScrollingChild } from '../../../util';
import vhCheck from 'vh-check';
import styles from './MobileNavMenu.module.scss';

// this calculates the offsets necessary for bottom positioning of
// buttons on the iphone. The Library imported here also provides
// the css variable --vh-offset
const vhIphoneAdjustment: {
  isNeeded: boolean;
  value: string | number;
  vh: number;
  windowHeight: number;
  offset: number;
  recompute: () => void;
  unbind: () => void;
} = vhCheck();

interface Props {
  isOpen?: boolean;
  isSticky?: boolean;
}

const MobileNavMenu: React.FC<Props> = ({ isOpen, isSticky }) => {
  const [firstOpen, setFirstOpen] = useState<boolean>(true);
  const [selectedNavCategory, setSelectedNavCategory] = useState<NavCategorySet | undefined>();
  const setSizingToolState = useSetRecoilState(sizingToolState);
  const pageContext = useRecoilValue(pageContextState);
  const assetPathFn = useRecoilValue(assetPathFnState);
  const setMenuOpen = useSetRecoilState(mobileMenuOpenState);
  const { product: selectedProduct } = useProductSelection(SelectionType.ProductSection);
  const { navCategorySets } = useNavCategories({ context: NavCategoryContext.MobileMenu });

  useEffect(() => {
    if (isOpen) {
      lockParentWithScrollingChild();
      if (firstOpen) {
        trackMobileNavMenuOpened();
        setFirstOpen(false);
      }
    } else {
      unlockParentWithScrollingChild();
    }
    return () => {
      unlockParentWithScrollingChild();
    };
  }, [isOpen]);

  const openSizingTool = () => {
    setSizingToolState({
      sizingToolState: SizingToolState.Open,
      selectionType: SelectionType.ProductSection,
      selectionKey: '0',
      product: pageContext === PageContext.ProductPage ? selectedProduct : undefined,
      initialProduct: pageContext === PageContext.ProductPage ? selectedProduct : undefined,
    });
    setMenuOpen(false);
  };

  return (
    <nav role="navigation">
      <div
        id="hl-primary-scrollable-menu"
        className={classNames(styles.hlMobileNavBody, {
          [styles.hlMobileNavBodyOpen]: isOpen,
          [styles.hlMobileStickyHeader]: isSticky,
          [styles.iphoneShift]: vhIphoneAdjustment.isNeeded,
        })}
      >
        <div className={styles.hlMobileNavCategories}>
          {navCategorySets.map((navCategorySet, index) => (
            <MobileNavMenuCategory
              navCategorySet={navCategorySet}
              selectCategory={setSelectedNavCategory}
              active={selectedNavCategory?.title === navCategorySet.title}
              index={index}
              key={index}
            />
          ))}
          <div className={classnames(styles.hlNavSublinkWrapper, styles.hlMobileNavCategory)}>
            <ImageButton
              dataTrackLabel="Find My Size"
              dataTrackLocation="mobile-menu"
              image={
                <div className={classNames(styles.hlMobileNavLink, styles.hlMobileNavIcon)} style={{ width: `100vw`, height: '100%' }}>
                  <div className={styles.hlProductMobileNavDescription}>
                    <span>Find My Size</span>
                  </div>
                  <div style={{ paddingRight: 26 }}>
                    <ImageLoader
                      src={assetPathFn('ruler-horizontal-regular.png')}
                      width="22"
                      height="11"
                      alt="find size icon"
                      style={{ filter: 'brightness(0%)' }}
                    />
                  </div>
                </div>
              }
              onClick={openSizingTool}
            />
          </div>
          {navLink(
            {
              text: 'Sculptwear quiz',
              href: '/pages/sculptwear-quiz',
              dataTrackLabel: 'Sculptwear quiz',
              dataTrackLocation: 'mobile-menu',
            },
            8999,
          )}
          {navLink(
            {
              text: 'Our Story',
              href: '/pages/about-us',
            },
            9000,
          )}
          <ClerkLoaded>
            <SignedOut>
              {navLink(
                {
                  text: 'My Rewards',
                  href: '/pages/loyalty',
                },
                9000,
              )}
            </SignedOut>
            <SignedIn>
              <SignedInControls />
            </SignedIn>
          </ClerkLoaded>
        </div>
      </div>
    </nav>
  );
};

export const navLink = (item: MenuItem, index: number) => {
  return (
    <div key={index} className={classnames(styles.hlNavSublinkWrapper, styles.hlMobileNavCategory)}>
      <a data-track-label={item.dataTrackLabel} data-track-location={item.dataTrackLocation} className={styles.hlMobileNavLink} href={item.href}>
        <div className={styles.hlProductMobileNavDescription}>
          <span>{item.text}</span>
        </div>
        <div />
      </a>
    </div>
  );
};

const SignedInControls: React.FC = () => {
  const { customer } = useCustomer();
  const { firstName, totalPoints } = customer;
  const { signOut } = useClerk();

  return (
    <>
      <div className={classnames(styles.hlNavSublinkWrapper, styles.hlMobileNavCategory, styles.clerkAccount)} style={{ position: 'relative' }}>
        <Link
          dataTrackLabel="My Profile"
          dataTrackLocation="mobile-menu"
          href="/pages/profile"
          noMargin={true}
          fullWidth={true}
          labelStyleOptions={{ uppercase: true }}
        >
          <div className={styles.hlProductMobileNavDescription}>
            <span>
              Hi {firstName}, <label style={{ color: '#dc6050' }}>{totalPoints} credits</label>
            </span>
          </div>
        </Link>
        <UserButton />
      </div>
      <div className={classnames(styles.hlNavSublinkWrapper, styles.hlMobileNavCategory, styles.clerkAccount)}>
        <Button
          dataTrackLabel="Log out"
          dataTrackLocation="mobile-menu"
          buttonType="link"
          onClick={() => signOut()}
          text="Log out"
          noMargin={true}
          fullWidth={true}
          labelStyleOptions={{ uppercase: true }}
        />
      </div>
    </>
  );
};

export default MobileNavMenu;
